import { useContext, useState } from "react";
import TenantContext from "../../context/tenant/TenantContext";
import AuthContext from "../../context/auth/AuthContext";
import { toast } from "../../helper/common";
import joinStyles from "../JoinApplicationScreen/JoinApplicationScreen.module.css";
import createStyles from "../../pages/onboarding/CreateApplicationStep.module.css";
import styles from "./GuarantorDecisionScreen.module.css";

type GuarantorDecisionScreenProps = {
  show: boolean;
  // First pending guarantee request (OnboardingMain slices first-only, same
  // rule as the legacy popup).
  request: any;
  loadPendingGuarantees: () => Promise<void>;
  loading: boolean;
  // Called after a successful accept so the parent can open the G result.
  onAccept: () => void;
  setAcceptedGuaranteeName: (name: string) => void;
  _t: (key: string, fallback?: string, ns?: string, args?: any) => string;
};

// D decision screen for cases 12-13 (tenant flow, end of signup): the tenant
// named in the request asks the user to back their application.
// Accept links the guarantee (linkGurantor), decline rejects it
// (rejectGuarantee, which emails the tenant). Same API contract as the legacy
// PendingRequestsPopup, without the consent checkbox block.
const GuarantorDecisionScreen = ({
  show,
  request,
  loadPendingGuarantees,
  loading,
  onAccept,
  setAcceptedGuaranteeName,
  _t,
}: GuarantorDecisionScreenProps) => {
  const { linkGurantor, rejectGuarantee }: any = useContext(TenantContext);
  const { loadUserTenant }: any = useContext(AuthContext);
  const [processing, setProcessing] = useState<"accept" | "decline" | null>(
    null
  );

  if (!show || !request) return null;

  const tenant = request?.tenant_id ?? {};
  const firstName = tenant.fname || (tenant.email ?? "").split("@")[0] || "";
  const fullName =
    `${tenant.fname ?? ""} ${tenant.lname ?? ""}`.trim() || firstName;
  // No gender data exists on the tenant record; translators own the final
  // wording (e.g. Spanish "su" is neutral).
  const possessive = "his";
  const subject = "he";

  const handleAccept = async () => {
    if (processing) return;
    const tenantId = tenant._id;
    if (!tenantId) return;
    setProcessing("accept");
    const userId = localStorage.getItem("saveduserid");
    try {
      await linkGurantor(userId, tenantId);
      loadUserTenant("tenant");
      setAcceptedGuaranteeName(fullName);
      await loadPendingGuarantees();
      onAccept();
    } catch (e: any) {
      toast.error(e?.response?.data?.msg ?? _t("failedToAcceptRequest"));
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async () => {
    if (processing) return;
    const tenantId = tenant._id;
    if (!tenantId) return;
    setProcessing("decline");
    try {
      await rejectGuarantee(tenantId);
      await loadPendingGuarantees();
    } catch {
      toast.error(_t("failedToRejectRequest"));
    } finally {
      setProcessing(null);
    }
  };

  const busy = processing !== null || loading;

  return (
    <div className={`${createStyles.overlay} modal-fixed-screen`}>
      <main
        className={`${createStyles.card} ${styles.shell} onboarding-modal-surface`}
      >
        <div className={styles.avatar}>
          {(firstName || "G").charAt(0).toUpperCase()}
        </div>
        <div className={styles.heading}>
          <h1>
            {_t("guarantorDecisionTitle", "", undefined, {
              first: firstName,
              possessive,
            })}
          </h1>
          <p className={createStyles.intro}>
            {_t("guarantorDecisionSubtitle", "", undefined, {
              full: fullName,
              possessive,
            })}
          </p>
        </div>

        <section className={styles.infoBox} aria-label="guarantor terms">
          <p className={styles.infoTitle}>
            {_t("guarantorDecisionBoxTitle", "", undefined, {
              first: firstName,
            })}
          </p>
          <div className={styles.infoRow}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="6.5" width="18" height="12" rx="2.5" />
              <path d="M3 10.5h18" />
              <circle cx="16.8" cy="14.8" r="1.1" />
            </svg>
            <span>
              {_t("guarantorDecisionBackRent", "", undefined, {
                first: firstName,
              })}
            </span>
          </div>
          <div className={styles.infoRow}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4.5 11.5 12 4.5l7.5 7" />
              <path d="M6.5 10v9.5h11V10" />
            </svg>
            <span>{_t("guarantorDecisionNoLive")}</span>
          </div>
          <div className={styles.infoRow}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="6.5" cy="12" r="2.3" />
              <circle cx="17" cy="6" r="2.3" />
              <circle cx="17" cy="18" r="2.3" />
              <path d="M8.6 10.9 14.9 7M8.6 13.1l6.3 3.9" />
            </svg>
            <span>
              {_t("guarantorDecisionProfileReach", "", undefined, {
                first: firstName,
                subject,
              })}
            </span>
          </div>
          <p className={styles.infoMuted}>
            {_t("guarantorDecisionStopBacking", "", undefined, {
              first: firstName,
            })}
          </p>
        </section>

        <footer className={`${joinStyles.footer} ${joinStyles.footerDecision}`}>
          <div className={`${joinStyles.decisionButtons} ${styles.actions}`}>
            <button
              type="button"
              className={joinStyles.declineButton}
              onClick={handleDecline}
              disabled={busy}
            >
              {processing === "decline" ? "..." : _t("declineInvitation")}
            </button>
            <button type="button" onClick={handleAccept} disabled={busy}>
              {processing === "accept" ? "..." : _t("acceptInvitation")}
            </button>
          </div>
          <p className={joinStyles.manageLater}>
            {_t("manageChangesLaterGuarantorsTab")}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default GuarantorDecisionScreen;
